import {
  useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import RadioButton from 'components/UI/Radiobutton';
import Switch from 'components/UI/Switch';
import Modal from 'components/Modal';
import { ReactComponent as LocationPinIcon } from 'assets/icons/digital-loan-location-pin.svg';
import { LOCATION_OPTIONS } from 'constants/digitalLoan';
import {
  acquireCurrentCoordinates,
  canEnableValidate,
  formatPostalCode,
  isValidPostalCode,
  requiresCompanyOrBusinessName,
} from '../utils/locationHelpers';

const LocationScreen = ({
  literals,
  initialValues,
  onValidate,
  onBack,
  isSubmitting,
}) => {
  const [locationOption, setLocationOption] = useState(initialValues?.locationType || '');
  const [companyOrBusinessName, setCompanyOrBusinessName] = useState(
    initialValues?.companyOrBusinessName || '',
  );
  const [postalCode, setPostalCode] = useState(initialValues?.locationPostalCode || '');
  const [locationAuthorized, setLocationAuthorized] = useState(
    !!initialValues?.coordinates,
  );
  const [coordinates, setCoordinates] = useState(initialValues?.coordinates || null);
  const [showLocationUnavailableModal, setShowLocationUnavailableModal] = useState(false);
  const [isAcquiringLocation, setIsAcquiringLocation] = useState(false);

  const radioOptions = useMemo(() => ([
    { value: LOCATION_OPTIONS.LIVE_NEARBY, label: literals.options.live },
    { value: LOCATION_OPTIONS.WORK_NEARBY, label: literals.options.work },
    { value: LOCATION_OPTIONS.BUSINESS_NEARBY, label: literals.options.business },
    { value: LOCATION_OPTIONS.UNSURE, label: literals.options.unsure },
  ]), [literals.options]);

  const showCompanyField = requiresCompanyOrBusinessName(locationOption);

  const postalCodeError = postalCode.length >= 1 && !isValidPostalCode(postalCode)
    ? literals.postalCodeError
    : false;

  const canValidate = canEnableValidate({
    locationOption,
    postalCode,
    locationAuthorized,
    coordinates,
    companyOrBusinessName,
    isSubmitting,
    isAcquiringLocation,
  });

  useEffect(() => {
    if (!requiresCompanyOrBusinessName(locationOption)) {
      setCompanyOrBusinessName('');
    }
  }, [locationOption]);

  useEffect(() => {
    let cancelled = false;

    if (!locationAuthorized) {
      setCoordinates(null);
      setIsAcquiringLocation(false);
      return undefined;
    }

    if (coordinates) {
      return undefined;
    }

    setIsAcquiringLocation(true);

    acquireCurrentCoordinates().then((result) => {
      if (cancelled) return;

      setIsAcquiringLocation(false);
      if (result.error) {
        setCoordinates(null);
        setShowLocationUnavailableModal(true);
        setLocationAuthorized(false);
        return;
      }

      setCoordinates({ latitude: result.latitude, longitude: result.longitude });
    });

    return () => {
      cancelled = true;
    };
  }, [locationAuthorized, coordinates]);

  const handlePostalCodeChange = (value) => {
    setPostalCode(formatPostalCode(value));
  };

  const handlePostalCodeBlur = () => {
    setPostalCode((current) => formatPostalCode(current));
  };

  const handleLocationToggle = () => {
    setLocationAuthorized((current) => !current);
  };

  const handleValidate = () => {
    if (!canValidate || !coordinates) return;
    onValidate({
      locationOption,
      companyOrBusinessName: showCompanyField ? companyOrBusinessName.trim() : '',
      postalCode,
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
    });
  };

  return (
    <div className='full-screen-container digital-loan-location'>
      <div className='screen-content'>
        <h1 className='digital-loan-location__title'>{literals.title}</h1>
        <p className='digital-loan-location__section-title font-bold'>
          {literals.proximityQuestion}
        </p>
        <p className='digital-loan-location__section-text'>{literals.proximityHint}</p>
        <RadioButton
          className='digital-loan-location__radios'
          value={locationOption}
          options={radioOptions}
          onChange={setLocationOption}
          disabled={isSubmitting}
        />
        {showCompanyField && (
          <div className='digital-loan-location__conditional-field'>
            <Input
              name='companyOrBusinessName'
              label={literals.businessNameLabel}
              placeholder={literals.businessNameLabel}
              value={companyOrBusinessName}
              onChange={setCompanyOrBusinessName}
              disabled={isSubmitting}
              required
            />
          </div>
        )}
        <p className='digital-loan-location__section-title digital-loan-location__section-title--postal font-bold'>
          {literals.postalQuestion}
        </p>
        <div className='digital-loan-location__postal-field'>
          <Input
            label={literals.postalLabel}
            placeholder={literals.postalLabel}
            value={postalCode}
            onChange={handlePostalCodeChange}
            onBlur={handlePostalCodeBlur}
            error={postalCodeError}
            inputMode='numeric'
            disabled={isSubmitting}
            required
          />
        </div>
        <p className='digital-loan-location__section-title digital-loan-location__section-title--location font-bold'>
          {literals.locationHelpTitle}
        </p>
        <p className='digital-loan-location__section-text digital-loan-location__section-text--location'>
          {literals.locationHelpText}
        </p>
        <div className='digital-loan-location__permission-row'>
          <div className='digital-loan-location__permission-label'>
            <LocationPinIcon className='digital-loan-location__permission-icon' aria-hidden='true' />
            <span>{literals.locationAuth}</span>
          </div>
          <Switch
            checked={locationAuthorized}
            onChange={handleLocationToggle}
            disabled={isSubmitting || isAcquiringLocation}
          />
        </div>
      </div>
      <div className='screen-actions digital-loan-location__actions'>
        <Button
          text={literals.validate}
          disabled={!canValidate}
          loading={isSubmitting}
          onClick={handleValidate}
          limitToOneClick
        />
        <Button
          text={literals.back}
          type='secondary'
          onClick={onBack}
          disabled={isSubmitting}
        />
      </div>

      {showLocationUnavailableModal && (
        <Modal
          className='digital-loan-location-unavailable-modal'
          position='center'
          closeOnBackgroundClick={false}
          showCloseButton={false}
          onClose={() => {}}
        >
          <div className='digital-loan-location-unavailable-modal__content'>
            <p className='digital-loan-location-unavailable-modal__body'>
              {literals.locationUnavailable}
            </p>
            <Button
              text={literals.accept}
              onClick={() => setShowLocationUnavailableModal(false)}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

LocationScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  initialValues: PropTypes.shape({
    locationType: PropTypes.string,
    companyOrBusinessName: PropTypes.string,
    locationPostalCode: PropTypes.string,
    coordinates: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
  }),
  onValidate: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

export default LocationScreen;
